import { SearchCompanySortQueries, CompaniesPageCategory } from '@constants_iqfu';
import { ExcludeLiteral } from '@utils_common/excludeLiteral';

import {
  PrerenderCompanyCitiesAnyProperties,
  PrerenderCompanyCitiesDefaultPageList,
  PrerenderCompanyCitiesDivorce,
  PrerenderCompanyCitiesHouse,
  PrerenderCompanyCitiesInheritance,
  PrerenderCompanyCitiesLeaseback,
  PrerenderCompanyCitiesLand,
  PrerenderCompanyCitiesMansion,
  PrerenderCompanyCitiesPurchase,
  PrerenderCompanyCitiesSellFast,
  PrerenderCompanyCitiesSellHigh,
  PrerenderPrefecturesCodeList,
  PrerenderCompanyCitiesAkiya,
} from './PrerenderPagesData';

type CompaniesPageCategoryKeys = keyof typeof CompaniesPageCategory;
type CompaniesPagesData = {
  country: {
    [key in CompaniesPageCategoryKeys]: Omit<CompaniesPageParameter, 'prerender_pages_list'>;
  };
  prefecture: {
    [key in CompaniesPageCategoryKeys]: CompaniesPageParameter;
  };
  city: {
    [key in CompaniesPageCategoryKeys]: CompaniesPageParameter;
  };
};

//path　defaultは空文字
export type CompaniesPageQueryType = ExcludeLiteral<CompaniesPageCategoryKeys, 'default'> | '';

const defaultCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sale_case_mediation',
  sale_cases_query: undefined,
  page_query_type: '',
  query_type_string: CompaniesPageCategory.default,
  property_type_string: undefined,
};

const anyPropertiesCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'not_decline',
  sale_cases_query: undefined,
  page_query_type: 'any_properties',
  query_type_string: CompaniesPageCategory.any_properties,
  property_type_string: undefined,
};

const divorceCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'divorce',
  sale_cases_query: undefined,
  page_query_type: 'divorce',
  query_type_string: CompaniesPageCategory.divorce,
  property_type_string: undefined,
};

const houseCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sale_case_house',
  sale_cases_query: undefined,
  page_query_type: 'house',
  query_type_string: CompaniesPageCategory.house,
  property_type_string: '一戸建て',
};

const inheritanceCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'inheritance',
  sale_cases_query: undefined,
  page_query_type: 'inheritance',
  query_type_string: CompaniesPageCategory.inheritance,
  property_type_string: undefined,
};

const leasebackCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'leaseback',
  sale_cases_query: undefined,
  page_query_type: 'leaseback',
  query_type_string: CompaniesPageCategory.leaseback,
  property_type_string: undefined,
};

const akiyaCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'akiya',
  sale_cases_query: undefined,
  page_query_type: 'akiya',
  query_type_string: CompaniesPageCategory.akiya,
  property_type_string: undefined,
};

const landCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sale_case_land',
  sale_cases_query: undefined,
  page_query_type: 'land',
  query_type_string: CompaniesPageCategory.land,
  property_type_string: '土地',
};

const mansionCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sale_case_mansion',
  sale_cases_query: undefined,
  page_query_type: 'mansion',
  query_type_string: CompaniesPageCategory.mansion,
  property_type_string: 'マンション',
};

const purchaseCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sale_case_purchase',
  sale_cases_query: undefined,
  page_query_type: 'purchase',
  query_type_string: CompaniesPageCategory.purchase,
  property_type_string: undefined,
};

const sellFastCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sell_fast',
  sale_cases_query: undefined,
  page_query_type: 'sell_fast',
  query_type_string: CompaniesPageCategory.sell_fast,
  property_type_string: undefined,
};

const sellHighCompaniesPageParameter: Omit<CompaniesPageParameter, 'page_label' | 'prerender_pages_list'> = {
  companies_sort_query: 'sell_high',
  sale_cases_query: undefined,
  page_query_type: 'sell_high',
  query_type_string: CompaniesPageCategory.sell_high,
  property_type_string: undefined,
};

export const CompaniesPagesData: CompaniesPagesData = {
  country: {
    default: {
      page_label: 'companies-default-country',
      ...defaultCompaniesPageParameter,
    },
    any_properties: {
      page_label: 'companies-any-properties-country',
      ...anyPropertiesCompaniesPageParameter,
    },
    divorce: {
      page_label: 'companies-divorce-country',
      ...divorceCompaniesPageParameter,
    },
    house: {
      page_label: 'companies-house-country',
      ...houseCompaniesPageParameter,
    },
    inheritance: {
      page_label: 'companies-inheritance-country',
      ...inheritanceCompaniesPageParameter,
    },
    land: {
      page_label: 'companies-land-country',
      ...landCompaniesPageParameter,
    },
    mansion: {
      page_label: 'companies-mansion-country',
      ...mansionCompaniesPageParameter,
    },
    purchase: {
      page_label: 'companies-purchase-country',
      ...purchaseCompaniesPageParameter,
    },
    sell_fast: {
      page_label: 'companies-sell-fast-country',
      ...sellFastCompaniesPageParameter,
    },
    sell_high: {
      page_label: 'companies-sell-high-country',
      ...sellHighCompaniesPageParameter,
    },
    leaseback: {
      page_label: 'companies-leaseback-country',
      ...leasebackCompaniesPageParameter,
    },
    akiya: {
      page_label: 'companies-akiya-country',
      ...akiyaCompaniesPageParameter,
    },
  },
  prefecture: {
    default: {
      page_label: 'companies-default-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...defaultCompaniesPageParameter,
    },
    any_properties: {
      page_label: 'companies-any-properties-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...anyPropertiesCompaniesPageParameter,
    },
    divorce: {
      page_label: 'companies-divorce-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...divorceCompaniesPageParameter,
    },
    house: {
      page_label: 'companies-house-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...houseCompaniesPageParameter,
    },
    inheritance: {
      page_label: 'companies-inheritance-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...inheritanceCompaniesPageParameter,
    },
    land: {
      page_label: 'companies-land-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...landCompaniesPageParameter,
    },
    mansion: {
      page_label: 'companies-mansion-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...mansionCompaniesPageParameter,
    },
    purchase: {
      page_label: 'companies-purchase-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...purchaseCompaniesPageParameter,
    },
    sell_fast: {
      page_label: 'companies-sell-fast-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...sellFastCompaniesPageParameter,
    },
    sell_high: {
      page_label: 'companies-sell-high-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...sellHighCompaniesPageParameter,
    },
    leaseback: {
      page_label: 'companies-leaseback-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...leasebackCompaniesPageParameter,
    },
    akiya: {
      page_label: 'companies-akiya-prefectures',
      prerender_pages_list: PrerenderPrefecturesCodeList,
      ...akiyaCompaniesPageParameter,
    },
  },
  city: {
    default: {
      page_label: 'companies-default-cities',
      prerender_pages_list: PrerenderCompanyCitiesDefaultPageList,
      ...defaultCompaniesPageParameter,
    },
    any_properties: {
      page_label: 'companies-any-properties-cities',
      prerender_pages_list: PrerenderCompanyCitiesAnyProperties,
      ...anyPropertiesCompaniesPageParameter,
    },
    divorce: {
      page_label: 'companies-divorce-cities',
      prerender_pages_list: PrerenderCompanyCitiesDivorce,
      ...divorceCompaniesPageParameter,
    },
    house: {
      page_label: 'companies-house-cities',
      prerender_pages_list: PrerenderCompanyCitiesHouse,
      ...houseCompaniesPageParameter,
    },
    inheritance: {
      page_label: 'companies-inheritance-cities',
      prerender_pages_list: PrerenderCompanyCitiesInheritance,
      ...inheritanceCompaniesPageParameter,
    },
    land: {
      page_label: 'companies-land-cities',
      prerender_pages_list: PrerenderCompanyCitiesLand,
      ...landCompaniesPageParameter,
    },
    mansion: {
      page_label: 'companies-mansion-cities',
      prerender_pages_list: PrerenderCompanyCitiesMansion,
      ...mansionCompaniesPageParameter,
    },
    purchase: {
      page_label: 'companies-purchase-cities',
      prerender_pages_list: PrerenderCompanyCitiesPurchase,
      ...purchaseCompaniesPageParameter,
    },
    sell_fast: {
      page_label: 'companies-sell-fast-cities',
      prerender_pages_list: PrerenderCompanyCitiesSellFast,
      ...sellFastCompaniesPageParameter,
    },
    sell_high: {
      page_label: 'companies-sell-high-cities',
      prerender_pages_list: PrerenderCompanyCitiesSellHigh,
      ...sellHighCompaniesPageParameter,
    },
    leaseback: {
      page_label: 'companies-leaseback-cities',
      prerender_pages_list: PrerenderCompanyCitiesLeaseback,
      ...leasebackCompaniesPageParameter,
    },
    akiya: {
      page_label: 'companies-akiya-cities',
      prerender_pages_list: PrerenderCompanyCitiesAkiya,
      ...akiyaCompaniesPageParameter,
    },
  },
} as const;

export type CompaniesPageParameter = {
  page_label: string;
  prerender_pages_list: readonly number[];
  companies_sort_query: keyof typeof SearchCompanySortQueries | undefined;
  sale_cases_query: undefined | 'land' | 'house' | 'mansion' | 'used_house,new_house';
  page_query_type: CompaniesPageQueryType;
  //any_propertiesの検索会社一覧取得queryは'not_decline'になる
  query_type_string: typeof CompaniesPageCategory[keyof typeof CompaniesPageCategory];
  property_type_string: undefined | '一戸建て' | '土地' | 'マンション';
};
